import { detectNaturalCaptureInputShape } from './naturalCaptureChunkingCore';

/**
 * Imported document natural-capture adapter.
 *
 * Thin contract wrapper that chooses the imported-document path, keeps rows
 * canonical, and exposes debug metadata without changing parser behavior.
 *
 * @param {import('./naturalCaptureContract').NaturalCaptureAdapterInput & { text?: string }} adapterInput
 * @param {Object} options
 * @param {Object} deps
 * @returns {import('./naturalCaptureContract').NaturalCaptureAdapterResult}
 */
export const runNaturalCaptureImportedDocAdapter = (adapterInput = {}, {
  defaultDate = new Date(),
  childName = null,
  parserMode = 'file_import_text',
  sourceMode = 'docx_xlsx',
  inputMode = 'file_import',
  rawText = '',
} = {}, deps = {}) => {
  const text = String(adapterInput.text || '').trim();
  const canonicalRawText = String(rawText || text || '').trim();
  if (!text) {
    return {
      rows: [],
      debug: {
        parserUsed: null,
        parserMode,
        sourceMode,
        inputMode,
        adapterMode: 'imported_file_text',
        inputType: 'unsupported',
        reason: 'empty_text',
        rawTextLength: 0,
      },
    };
  }

  const {
    buildNaturalCaptureLongNoteRows,
    buildNaturalCaptureSchoolRows,
    groupRowsForReview,
    normalizeImportedFileReviewRows,
  } = deps || {};

  const inputShape = String(adapterInput.shapeHint || '').trim() || (
    typeof detectNaturalCaptureInputShape === 'function'
      ? detectNaturalCaptureInputShape(text, {
          adapterMode: adapterInput.adapterMode,
          parserMode,
          sourceMode,
          inputMode,
        })
      : 'school_text'
  );
  const usesLongNotePath = inputShape === 'long_note_email';
  const underlyingPath = usesLongNotePath
    ? 'local_long_note_parsing'
    : 'existing_import_parser_behavior';

  const sourceRows = usesLongNotePath
    ? (typeof groupRowsForReview === 'function' && typeof buildNaturalCaptureLongNoteRows === 'function'
      ? groupRowsForReview(buildNaturalCaptureLongNoteRows(text, {
          defaultDate,
          childName,
          enableDomainBoundarySplit: false,
        }))
      : [])
    : (typeof buildNaturalCaptureSchoolRows === 'function'
      ? buildNaturalCaptureSchoolRows(text, {
          defaultDate,
          childName,
        })
      : []);

  const rows = typeof normalizeImportedFileReviewRows === 'function'
    ? normalizeImportedFileReviewRows(sourceRows, {
        parserMode,
        sourceMode,
        inputMode,
        rawText: canonicalRawText || text,
      })
    : sourceRows;

  const categoryComparisons = [];
  const resolvedRows = rows;

  return {
    rows: resolvedRows,
    debug: {
      parserUsed: usesLongNotePath
        ? 'natural_capture_long_note_adapter'
        : 'natural_capture_school_adapter',
      parserMode,
      sourceMode,
      inputMode,
      adapterMode: 'imported_file_text',
      inputType: 'file_import',
      detectedShape: usesLongNotePath ? 'long_note_email' : 'school_text',
      chunkPlanSource: usesLongNotePath
        ? 'splitNaturalCaptureLongNoteBlocks'
        : 'buildSchoolEventChunks',
      underlyingPath,
      reason: usesLongNotePath ? 'long_note_email' : 'existing_import_parser_behavior',
      rawTextLength: canonicalRawText.length || text.length,
      rowCount: Array.isArray(resolvedRows) ? resolvedRows.length : 0,
      categoryComparisons,
      decisionAudit: categoryComparisons.map((decision, index) => ({
        index,
        originalCategory: decision?.originalCategory || null,
        proposedCategory: decision?.proposedCategory || null,
        finalCategory: decision?.finalCategory || null,
        promotionApplied: Boolean(decision?.promotionApplied),
        promotionReason: decision?.promotionReason || '',
        adapterMode: inputMode,
        detectedShape: usesLongNotePath ? 'long_note_email' : 'school_text',
        fallbackReason: decision?.reason?.[0] || '',
      })),
    },
  };
};
